/**
 * TanStack Query — la liste d'employés (chunk 9.BK.1).
 *
 * ## Une règle gouverne ce fichier
 *
 * **Aucune règle métier n'est recalculée ici.** Les manques (`issues`) et
 * l'état (`status`) viennent du serveur, qui les calcule avec la définition
 * partagée. L'écran les affiche. Une validation réécrite en TypeScript côté
 * navigateur finirait par diverger de celle qui refuse réellement — et c'est la
 * seconde qui compte.
 *
 * ## Deux surfaces, deux clients HTTP
 *
 * Le parcours client passe par `apiPublic` (aucune authentification, échec
 * uniforme) ; le parcours conseiller par `api` (JWT). Ils écrivent dans **le
 * même census** — il n'y a pas de copie, pas de voie parallèle.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { publicDelete, publicGet, publicPostJson, publicPutJson } from "../apiPublic";

// ---------------------------------------------------------------------------
// Formes de lecture
// ---------------------------------------------------------------------------

export type CensusIssue = {
  readonly row: "participant" | "dependent";
  readonly client_row_id: string;
  readonly field: string | null;
  readonly reason: string;
};

export type CensusParticipantRow = {
  readonly client_row_id: string;
  readonly employee_number: string | null;
  readonly first_name: string | null;
  readonly last_name: string | null;
  readonly birth_date: string | null;
  readonly gender: string | null;
  readonly mobile_phone: string | null;
  readonly email: string | null;
  readonly additional_email: string | null;
  readonly additional_phone: string | null;
  readonly hire_date: string | null;
  readonly employment_type: string | null;
  readonly division: string | null;
  readonly occupation: string | null;
  readonly annual_salary: string | number | null;
  readonly hourly_rate: string | number | null;
  readonly hours_per_week: string | number | null;
  readonly smoker: string | null;
  readonly coverage: string | null;
  readonly employment_status: string | null;
  readonly coverage_start: string | null;
  readonly coverage_end: string | null;
  readonly note: string | null;
  /**
   * Ce que le classeur disait et que le modèle n'a pas su lire, par champ —
   * `{"birth_date": "12 avril 85"}`. Absent d'une ligne saisie à la main.
   *
   * Tant qu'une clé est là, le champ typé est vide **parce que** la valeur était
   * illisible, et le serveur en fait un manque. Corriger le champ retire la clé.
   */
  readonly import_raw?: Record<string, string> | null;
};

export type CensusDependentRow = {
  readonly client_row_id: string;
  readonly participant_client_row_id: string;
  readonly first_name: string | null;
  readonly last_name: string | null;
  readonly gender: string | null;
  readonly relationship: string | null;
  readonly birth_date: string | null;
  readonly smoker: string | null;
  readonly coverage_start: string | null;
  readonly coverage_end: string | null;
  readonly dependent_status: string | null;
};

export type CensusStatus = "draft" | "submitted" | "locked";

export type PublicCensus = {
  readonly status: CensusStatus;
  readonly issues: readonly CensusIssue[];
  readonly company_name: string | null;
  readonly project_title: string | null;
  readonly limits: {
    readonly maxParticipants: number;
    readonly maxDependentsPerParticipant: number;
  };
  readonly participants: readonly CensusParticipantRow[];
  readonly dependents: readonly CensusDependentRow[];
};

/**
 * Ce que la page publique sait de son lien.
 *
 * `unavailable` couvre **toutes** les défaillances de jeton sans les
 * distinguer — expiré, révoqué, forgé, dossier sans liste, ou API muette. Les
 * séparer à l'écran annulerait l'anti-énumération du serveur.
 */
export type PublicCensusState =
  | { readonly kind: "ready"; readonly census: PublicCensus }
  | { readonly kind: "unavailable" };

export function usePublicCensus(token: string) {
  return useQuery<PublicCensusState>({
    queryKey: ["public-census", token],
    queryFn: async () => {
      try {
        const res = await publicGet<PublicCensus>(`/v1/public/census/${token}`);
        if (res.envelope.ok && res.envelope.data) {
          return { kind: "ready", census: res.envelope.data } as const;
        }
        return { kind: "unavailable" } as const;
      } catch {
        // Parité anti-énumération : « API muette » et « lien mort » se
        // ressemblent à l'écran, parce qu'ils doivent se ressembler.
        return { kind: "unavailable" } as const;
      }
    },
    retry: false,
    // La reprise, c'est simplement relire. Il n'y a pas de brouillon séparé à
    // restaurer : le census EST l'état persistant, rien n'a jamais été perdu.
    refetchOnWindowFocus: true,
  });
}

/** Le résultat d'une écriture publique, tel que l'écran doit le traiter. */
export type EcritureResultat =
  | { readonly kind: "ok" }
  | { readonly kind: "unavailable" }
  | { readonly kind: "refused"; readonly code: string; readonly issues?: readonly CensusIssue[] };

function interpreter(res: {
  status: number;
  envelope: { ok: boolean; error?: { code?: string; details?: unknown } };
}): EcritureResultat {
  if (res.envelope.ok) return { kind: "ok" };
  const code = res.envelope.error?.code ?? "UNKNOWN";
  if (code === "PUBLIC_INTAKE_LINK_UNAVAILABLE") return { kind: "unavailable" };
  const details = res.envelope.error?.details as { issues?: CensusIssue[] } | undefined;
  return { kind: "refused", code, issues: details?.issues };
}

export function usePublicCensusMutations(token: string) {
  const qc = useQueryClient();
  const rafraichir = () => qc.invalidateQueries({ queryKey: ["public-census", token] });

  const enregistrerParticipant = useMutation({
    mutationFn: async (v: { rowId: string; valeurs: Record<string, unknown> }) =>
      interpreter(
        await publicPutJson(`/v1/public/census/${token}/participants/${v.rowId}`, v.valeurs),
      ),
    onSuccess: rafraichir,
  });

  const retirerParticipant = useMutation({
    mutationFn: async (rowId: string) =>
      interpreter(await publicDelete(`/v1/public/census/${token}/participants/${rowId}`)),
    onSuccess: rafraichir,
  });

  const enregistrerDependant = useMutation({
    mutationFn: async (v: { rowId: string; valeurs: Record<string, unknown> }) =>
      interpreter(
        await publicPutJson(`/v1/public/census/${token}/dependents/${v.rowId}`, v.valeurs),
      ),
    onSuccess: rafraichir,
  });

  const retirerDependant = useMutation({
    mutationFn: async (rowId: string) =>
      interpreter(await publicDelete(`/v1/public/census/${token}/dependents/${rowId}`)),
    onSuccess: rafraichir,
  });

  /** « J'ai terminé » → `submitted`. Jamais `locked` : le verrou est au cabinet. */
  const declarerTermine = useMutation({
    mutationFn: async () =>
      interpreter(await publicPostJson(`/v1/public/census/${token}/complete`, {})),
    onSuccess: rafraichir,
  });

  return {
    enregistrerParticipant,
    retirerParticipant,
    enregistrerDependant,
    retirerDependant,
    declarerTermine,
  };
}

// ---------------------------------------------------------------------------
// Parcours conseiller
// ---------------------------------------------------------------------------

export type BrokerCensus = PublicCensus & {
  readonly id: string;
  readonly project_id: string;
  readonly locked_at: string | null;
  readonly submitted_at: string | null;
  /**
   * Qui a déclaré la collecte terminée. `submitted` ne le dit plus par
   * lui-même : le cabinet peut déclarer à la place du client quand celui-ci a
   * renvoyé son classeur par courriel.
   */
  readonly submitted_source?: "client" | "user" | null;
  /** Déclaré contre constaté. **Information seulement** — jamais un blocage. */
  readonly headcount: { readonly declared: number | null; readonly counted: number };
};

export function useProjectCensus(projectId: string) {
  return useQuery<BrokerCensus | null>({
    queryKey: ["census", projectId],
    queryFn: async () => {
      try {
        return await api.get<BrokerCensus>(`/v1/censuses?project_id=${projectId}`);
      } catch (err) {
        // Aucun census sur ce dossier n'est pas une erreur : c'est un état.
        if ((err as { status?: number }).status === 404) return null;
        throw err;
      }
    },
  });
}

export function useCensusActions(projectId: string) {
  const qc = useQueryClient();
  const rafraichir = () => {
    void qc.invalidateQueries({ queryKey: ["census", projectId] });
    // Le verrou change la complétude du dossier ET la readiness d'envoi.
    void qc.invalidateQueries({ queryKey: ["project-completeness", projectId] });
    void qc.invalidateQueries({ queryKey: ["quote-requests"] });
  };

  return {
    creer: useMutation({
      mutationFn: () => api.post<BrokerCensus>("/v1/censuses", { project_id: projectId }),
      onSuccess: rafraichir,
    }),
    enregistrerParticipant: useMutation({
      mutationFn: (v: { censusId: string; rowId: string; valeurs: Record<string, unknown> }) =>
        api.put(`/v1/censuses/${v.censusId}/participants/${v.rowId}`, v.valeurs),
      onSuccess: rafraichir,
    }),
    retirerParticipant: useMutation({
      mutationFn: (v: { censusId: string; rowId: string }) =>
        api.del(`/v1/censuses/${v.censusId}/participants/${v.rowId}`),
      onSuccess: rafraichir,
    }),
    creerLien: useMutation({
      mutationFn: (censusId: string) =>
        api.post<{ id: string; expires_at: string | null; census_url: string }>(
          `/v1/censuses/${censusId}/link`,
          {},
        ),
    }),
    declarerTermine: useMutation({
      mutationFn: (censusId: string) => api.post(`/v1/censuses/${censusId}/submit`, {}),
      onSuccess: rafraichir,
    }),
    verrouiller: useMutation({
      mutationFn: (censusId: string) => api.post(`/v1/censuses/${censusId}/lock`, {}),
      onSuccess: rafraichir,
    }),
    deverrouiller: useMutation({
      mutationFn: (v: { censusId: string; reason: string }) =>
        api.post(`/v1/censuses/${v.censusId}/unlock`, { reason: v.reason }),
      onSuccess: rafraichir,
    }),
    /**
     * Remettre en saisie — `submitted → draft`. Mini-fix 9.BK.3.
     *
     * Distinct du déverrouillage, et jamais enchaîné automatiquement avec lui :
     * un effectif verrouillé se déverrouille d'abord, par une décision séparée.
     */
    remettreEnSaisie: useMutation({
      mutationFn: (v: { censusId: string; reason: string }) =>
        api.post(`/v1/censuses/${v.censusId}/reopen`, { reason: v.reason }),
      onSuccess: rafraichir,
    }),
  };
}

// ---------------------------------------------------------------------------
// Import d'un classeur — chunk 9.BK.2
// ---------------------------------------------------------------------------

/**
 * Une ligne telle que le serveur l'a comprise, avec ce qu'il en pense.
 *
 * `issues` vient de la **même** règle que le verrouillage : l'écran ne rejuge
 * rien. `annual_salary_suggestion` voyage À CÔTÉ des valeurs, jamais dedans —
 * c'est ce qui la garde à l'état de proposition jusqu'à ce qu'une personne la
 * reprenne à son compte.
 */
export type CensusImportRow = {
  readonly ligne: number;
  readonly valeurs: Record<string, string | number | null>;
  readonly issues: ReadonlyArray<{ field: string | null; reason: string }>;
  readonly annual_salary_suggestion: number | null;
};

export type CensusImportPreview = {
  readonly file_id: string;
  readonly sheet_name: string;
  readonly header_row: number;
  readonly columns_recognised: ReadonlyArray<{ colonne: string; champ: string }>;
  readonly unknown_columns: readonly string[];
  readonly rows_detected: number;
  /** Combien de lignes le remplacement effacerait. Dit AVANT de remplacer. */
  readonly rows_existing: number;
  readonly anomalies: ReadonlyArray<{
    readonly ligne: number;
    readonly champ: string | null;
    readonly motif: string;
    readonly valeur: string | null;
  }>;
  readonly rows: readonly CensusImportRow[];
};

export function useCensusImport(projectId: string) {
  const qc = useQueryClient();

  return {
    /** Analyse le classeur. **N'écrit rien** — aucune ligne ne bouge encore. */
    analyser: useMutation({
      mutationFn: (v: { censusId: string; fileId: string }) =>
        api.post<CensusImportPreview>(`/v1/censuses/${v.censusId}/import/preview`, {
          file_id: v.fileId,
        }),
    }),

    /** Applique le remplacement, avec les lignes que le conseiller a retenues. */
    appliquer: useMutation({
      mutationFn: (v: { censusId: string; corps: Record<string, unknown> }) =>
        api.post<{ rows_applied: number; rows_replaced: number }>(
          `/v1/censuses/${v.censusId}/import/apply`,
          v.corps,
        ),
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["census", projectId] });
        void qc.invalidateQueries({ queryKey: ["project-completeness", projectId] });
      },
    }),
  };
}
